/**
 * Wrapper that inspects the AI response and renders the appropriate UI component:
 * table | cards | form | message.
 * Supports both structured payload (type, title, data, actions) and legacy tool_payload (ui_type).
 * Enquiry lists render using the same table as AllEnquiries (EnquiryList).
 */
import React from "react";
import AIResponseTable from "./AIResponseTable";
import AIResponseCards from "./AIResponseCards";
import AIResponseForm from "./AIResponseForm";
import AIResponseMessage from "./AIResponseMessage";

function tryParseStructured(content) {
  if (content == null) return null;
  if (typeof content === "object" && content !== null && "type" in content) return content;
  if (typeof content !== "string") return null;
  const trimmed = content.trim();
  if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed.type === "string") return parsed;
    } catch (_) {}
  }
  return null;
}

const DEFAULT_ENQUIRY_COLUMNS = [
  { key: "id", label: "ID" },
  { key: "first_name", label: "First Name" },
  { key: "last_name", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "phone_number", label: "Phone" },
  { key: "status", label: "Status" },
];

function columnsFromFirstRow(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return DEFAULT_ENQUIRY_COLUMNS;
  const first = rows[0];
  if (!first || typeof first !== "object") return DEFAULT_ENQUIRY_COLUMNS;
  return Object.keys(first).map((k) => ({
    key: k,
    label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  }));
}

function mapLegacyPayloadToStructured(payload) {
  if (!payload || typeof payload !== "object") return null;
  if (payload.type) return payload;
  if (payload.ui_type === "enquiry_list" && Array.isArray(payload.data)) {
    return {
      type: "table",
      title: "Enquiries",
      columns: columnsFromFirstRow(payload.data),
      rows: payload.data,
      rowClickNavigate: "enquiry_profile",
    };
  }
  if (payload.ui_type === "create_enquiry_form" && Array.isArray(payload.fields)) {
    return {
      type: "form",
      title: "Create Enquiry",
      fields: payload.fields,
      submit_tool: "create_enquiry",
    };
  }
  return null;
}

export default function AIResponseRenderer({
  response,
  conversationId,
  onFormSubmit,
  onAction,
  onSuggestionClick,
}) {
  let structured = tryParseStructured(response) ?? mapLegacyPayloadToStructured(response);

  // Ensure table payloads always have columns (derive from first row if missing)
  if (structured?.type === "table") {
    const rows = structured.rows ?? structured.data ?? [];
    if ((!structured.columns || structured.columns.length === 0) && Array.isArray(rows) && rows.length > 0) {
      structured = { ...structured, columns: columnsFromFirstRow(rows) };
    }
  }

  // Enquiry list: use AIResponseTable (same controls: Export Excel/CSV, Copy, Show more, Hide columns)
  const isEnquiryList =
    (structured?.type === "table" && structured?.rowClickNavigate === "enquiry_profile") ||
    (response?.ui_type === "enquiry_list" && Array.isArray(response?.data));
  const enquiryRows = isEnquiryList
    ? (structured?.rows ?? structured?.data ?? response?.data ?? [])
    : [];

  const followUps = structured?.follow_ups ?? structured?.suggestions;

  if (isEnquiryList && enquiryRows.length >= 0) {
    return (
      <AIResponseTable
        title={structured?.title || "Enquiries"}
        columns={structured?.columns || DEFAULT_ENQUIRY_COLUMNS}
        rows={enquiryRows}
        rowClickNavigate="enquiry_profile"
        getRowId={(row) => row.id}
        exportFilename="enquiries"
        suggestions={followUps}
        onSuggestionClick={onSuggestionClick}
      />
    );
  }

  if (structured && structured.type === "table") {
    const tableRows = structured.rows || structured.data || [];
    const tableColumns =
      structured.columns?.length > 0 ? structured.columns : columnsFromFirstRow(tableRows);
    return (
      <AIResponseTable
        title={structured.title}
        columns={tableColumns}
        rows={tableRows}
        actions={structured.actions || []}
        rowClickNavigate={structured.rowClickNavigate}
        getRowId={structured.getRowId ? (row) => structured.getRowId(row) : (row) => row.id}
        exportFilename={structured.exportFilename}
        suggestions={followUps}
        onSuggestionClick={onSuggestionClick}
      />
    );
  }

  if (structured && structured.type === "cards") {
    return (
      <AIResponseCards
        title={structured.title}
        cards={structured.cards || structured.data || []}
        onAction={onAction}
      />
    );
  }

  if (structured && structured.type === "form") {
    return (
      <AIResponseForm
        title={structured.title}
        fields={structured.fields || []}
        submit_tool={structured.submit_tool}
        submitLabel={structured.submitLabel || "Submit"}
        conversationId={conversationId}
        onFormSubmit={onFormSubmit}
      />
    );
  }

  if (structured && structured.type === "message") {
    return <AIResponseMessage content={structured.content ?? structured.data} />;
  }

  return <AIResponseMessage content={typeof response === "string" ? response : response?.content ?? ""} />;
}
